import { cn } from '../../lib/cn';

export interface StepperStep {
  id: string;
  label: string;
}

/**
 * Read-only progress indicator for a multi-step flow. Purely presentational:
 * the parent owns the current index and all navigation. `current` is the
 * zero-based index of the active step.
 */
export function Stepper({
  steps,
  current,
  className,
}: {
  steps: StepperStep[];
  current: number;
  className?: string;
}): JSX.Element {
  return (
    <ol
      aria-label="Progression"
      className={cn('flex flex-wrap items-center gap-2 text-sm', className)}
    >
      {steps.map((step, index) => {
        const status = index < current ? 'done' : index === current ? 'current' : 'upcoming';
        return (
          <li
            key={step.id}
            aria-current={status === 'current' ? 'step' : undefined}
            className="flex items-center gap-2"
          >
            <span
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                status === 'done' && 'bg-brand-600 text-white',
                status === 'current' && 'bg-brand-100 text-brand-700 ring-2 ring-brand-500',
                status === 'upcoming' && 'bg-slate-100 text-slate-400',
              )}
            >
              {status === 'done' ? '✓' : index + 1}
            </span>
            <span
              className={cn(
                'font-medium',
                status === 'current' ? 'text-slate-900' : 'text-slate-500',
              )}
            >
              {step.label}
            </span>
            {index < steps.length - 1 ? (
              <span aria-hidden="true" className="mx-1 text-slate-300">
                →
              </span>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
